import type { RefObject } from "react";
import { Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import { destinationStatement } from "@/lib/session-destination-copy";

/** The teaching team's way into the editor that opens in place below this line. */
export interface DestinationLineEdit {
  expanded: boolean;
  /** The editor's id while it is open. */
  controls: string;
  onToggle: () => void;
  toggleRef: RefObject<HTMLButtonElement | null>;
}

/**
 * Where this exact session meets, stated in the time tile (Bento fusion).
 *
 * One sentence from the server's resolution, plus the missing-room line a
 * hybrid session needs. For a teaching viewer the server lets edit it, the line
 * carries one quiet control that opens the editor in place below it; the join
 * control and the staff details are the time tile's to place. This block reads
 * no URL.
 */
export function DestinationPanel({
  workspace,
  teaching,
  edit,
}: {
  workspace: SessionWorkspace;
  teaching: boolean;
  edit?: DestinationLineEdit;
}) {
  const { destination } = workspace;
  const nothingToJoin = destination.resolution === "cancelled_no_destination" || destination.resolution === "none";
  const unavailable = nothingToJoin
    || destination.resolution === "needs_destination"
    || destination.resolution === "needs_repair";

  const statement = (
    <>
      <p
        role={unavailable ? "status" : undefined}
        className={unavailable ? "text-ui text-muted-foreground" : "text-ui font-medium text-foreground-soft"}
      >
        {destinationStatement(workspace, teaching)}
      </p>
      {teaching && !nothingToJoin && destination.effective_source && <p className="text-meta text-muted-foreground">{destination.effective_source === "course_default" ? "Course link" : "This session only"}</p>}
      {destination.modality === "hybrid" && !destination.location && !nothingToJoin && (
        <p className="text-meta text-muted-foreground">No room is recorded for the in-person half.</p>
      )}
    </>
  );

  if (!edit) {
    return (
      <section aria-label="Where this session meets" className="grid min-w-0 gap-0.5" data-session-destination-panel>
        {statement}
      </section>
    );
  }

  return (
    <section aria-label="Where this session meets" className="flex min-w-0 items-start gap-2" data-session-destination-panel>
      <div className="grid min-w-0 flex-1 gap-0.5">{statement}</div>
      <Button
        ref={edit.toggleRef}
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Edit where the session meets"
        aria-expanded={edit.expanded}
        aria-controls={edit.expanded ? edit.controls : undefined}
        className="-m-[0.4375rem] size-11 shrink-0 text-foreground-soft"
        onClick={edit.onToggle}
      >
        <Pencil aria-hidden className="size-4" strokeWidth={1.75} />
      </Button>
    </section>
  );
}
